import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import { createUser, getUsers, updateUser } from "../api";
import { useAuth } from "../auth/AuthProvider";
import ServerSideDataTable from "./ServerSideDataTable";

const DEFAULT_SORT_BY = "role";
const DEFAULT_SORT_INDEX = 2;

function UsersDataTable(_, ref) {
    const { withAuth, currentUser } = useAuth();
    const [tableKey, setTableKey] = useState(0);
    const [newUserEmail, setNewUserEmail] = useState("");
    const [newUserPassword, setNewUserPassword] = useState("");
    const [newUserRole, setNewUserRole] = useState("user");
    const [isCreatingUser, setIsCreatingUser] = useState(false);
    const [createUserError, setCreateUserError] = useState("");
    const [emailModalUser, setEmailModalUser] = useState(null);
    const [newEmail, setNewEmail] = useState("");
    const [isSavingEmail, setIsSavingEmail] = useState(false);
    const [passwordModalUser, setPasswordModalUser] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [isSavingPassword, setIsSavingPassword] = useState(false);
    const createModalRef = useRef(null);
    const emailModalRef = useRef(null);
    const modalRef = useRef(null);

    const refreshTable = useCallback(() => {
        setTableKey((current) => current + 1);
    }, []);

    const columns = [
        { data: "id", title: "ID" },
        {
            data: "email",
            title: "Email",
            render: (data, type, row) => {
                if (type !== "display") {
                    return data || "";
                }

                if (!data) {
                    return "-";
                }

                return `
                    <div class="d-inline-flex align-items-center gap-1">
                        <span>${data}</span>
                        <button
                            type="button"
                            class="btn btn-link btn-sm p-0 text-secondary js-edit-user-email"
                            data-user-id="${row?.id || ""}"
                            data-user-email="${data}"
                            title="Modifier l'email"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            aria-label="Modifier l'email"
                        >
                            <i class="bi bi-pencil" aria-hidden="true"></i>
                        </button>
                    </div>
                `;
            }
        },
        {
            data: "role",
            title: "Rôle",
            render: (data, type, row) => {
                const role = String(data || "user").toLowerCase();
                const isCurrentUser = String(row?.id || "") === String(currentUser?.id || "");
                if (type !== "display") {
                    return role;
                }

                return `
                    <select
                        class="form-select form-select-sm js-user-role"
                        data-user-id="${row?.id || ""}"
                        style="max-width: 120px;"
                        ${isCurrentUser ? "disabled" : ""}
                        ${isCurrentUser ? "title=\"Tu ne peux pas modifier ton propre rôle\"" : ""}
                    >
                        <option value="user" ${role === "user" ? "selected" : ""}>user</option>
                        <option value="admin" ${role === "admin" ? "selected" : ""}>admin</option>
                    </select>
                `;
            }
        },
        {
            data: "disabled_at",
            title: "Désactivé le",
            render: (data, type) => {
                if (type !== "display") {
                    return data || "";
                }

                return data ? new Date(data).toLocaleString() : "-";
            }
        },
        {
            data: "created_at",
            title: "Créé le",
            render: (data, type) => {
                if (type !== "display") {
                    return data || "";
                }

                return data ? new Date(data).toLocaleString() : "-";
            }
        },
        {
            data: "id",
            title: "Mot de passe",
            orderable: false,
            searchable: false,
            render: (data, type, row) => {
                if (type !== "display") {
                    return data || "";
                }

                return `
                    <button
                        type="button"
                        class="btn btn-outline-secondary btn-sm js-edit-user-password"
                        data-user-id="${row?.id || ""}"
                        data-user-email="${row?.email || ""}"
                    >
                        Modifier
                    </button>
                `;
            }
        }
    ];

    const openPasswordModal = useCallback((user) => {
        setPasswordModalUser(user);
        setNewPassword("");

        const Modal = window.bootstrap?.Modal;
        if (!Modal || !modalRef.current) {
            return;
        }

        const instance = Modal.getOrCreateInstance(modalRef.current);
        instance.show();
    }, []);

    const closePasswordModal = useCallback(() => {
        const Modal = window.bootstrap?.Modal;
        if (!Modal || !modalRef.current) {
            return;
        }

        const instance = Modal.getOrCreateInstance(modalRef.current);
        instance.hide();
    }, []);

    const openEmailModal = useCallback((user) => {
        setEmailModalUser(user);
        setNewEmail(user?.email || "");

        const Modal = window.bootstrap?.Modal;
        if (!Modal || !emailModalRef.current) {
            return;
        }

        const instance = Modal.getOrCreateInstance(emailModalRef.current);
        instance.show();
    }, []);

    const closeEmailModal = useCallback(() => {
        const Modal = window.bootstrap?.Modal;
        if (!Modal || !emailModalRef.current) {
            return;
        }

        const instance = Modal.getOrCreateInstance(emailModalRef.current);
        instance.hide();
    }, []);

    const openCreateUserModal = useCallback(() => {
        setNewUserEmail("");
        setNewUserPassword("");
        setNewUserRole("user");
        setCreateUserError("");

        const Modal = window.bootstrap?.Modal;
        if (!Modal || !createModalRef.current) {
            return;
        }

        const instance = Modal.getOrCreateInstance(createModalRef.current);
        instance.show();
    }, []);

    const closeCreateUserModal = useCallback(() => {
        const Modal = window.bootstrap?.Modal;
        if (!Modal || !createModalRef.current) {
            return;
        }

        const instance = Modal.getOrCreateInstance(createModalRef.current);
        instance.hide();
    }, []);

    const registerDomHandlers = useCallback(({ wrapper, setError }) => {
        const onChange = async (event) => {
            const roleSelect = event.target.closest(".js-user-role");
            if (!roleSelect || !wrapper.contains(roleSelect)) {
                return;
            }

            const userId = Number.parseInt(roleSelect.getAttribute("data-user-id") || "", 10);
            if (String(userId) === String(currentUser?.id || "")) {
                roleSelect.disabled = true;
                return;
            }

            const role = String(roleSelect.value || "").toLowerCase();
            if (!Number.isInteger(userId) || userId <= 0 || !["admin", "user"].includes(role)) {
                return;
            }

            try {
                setError("");
                roleSelect.disabled = true;
                await withAuth((token) => updateUser(token, userId, { role }));
                refreshTable();
            } catch {
                setError("Impossible de mettre à jour le rôle de l'utilisateur.");
            } finally {
                roleSelect.disabled = false;
            }
        };

        const onClick = async (event) => {
            const emailButton = event.target.closest(".js-edit-user-email");
            if (emailButton && wrapper.contains(emailButton)) {
                event.preventDefault();

                const userId = Number.parseInt(emailButton.getAttribute("data-user-id") || "", 10);
                const currentEmail = emailButton.getAttribute("data-user-email") || "";
                if (!Number.isInteger(userId) || userId <= 0) {
                    return;
                }

                openEmailModal({ id: userId, email: currentEmail });

                return;
            }

            const passwordButton = event.target.closest(".js-edit-user-password");
            if (!passwordButton || !wrapper.contains(passwordButton)) {
                return;
            }

            event.preventDefault();
            const userId = Number.parseInt(passwordButton.getAttribute("data-user-id") || "", 10);
            const userEmail = passwordButton.getAttribute("data-user-email") || "";
            if (!Number.isInteger(userId) || userId <= 0) {
                return;
            }

            openPasswordModal({ id: userId, email: userEmail });
        };

        wrapper.addEventListener("change", onChange);
        wrapper.addEventListener("click", onClick);

        return () => {
            wrapper.removeEventListener("change", onChange);
            wrapper.removeEventListener("click", onClick);
        };
    }, [currentUser?.id, openEmailModal, openPasswordModal, refreshTable, withAuth]);

    const handleEmailSubmit = async (event) => {
        event.preventDefault();

        if (!emailModalUser?.id || !newEmail.trim() || isSavingEmail) {
            return;
        }

        if (newEmail.trim() === String(emailModalUser.email || "").trim()) {
            closeEmailModal();
            return;
        }

        try {
            setIsSavingEmail(true);
            await withAuth((token) => updateUser(token, emailModalUser.id, { email: newEmail.trim() }));
            closeEmailModal();
            setEmailModalUser(null);
            setNewEmail("");
            refreshTable();
        } finally {
            setIsSavingEmail(false);
        }
    };

    const handlePasswordSubmit = async (event) => {
        event.preventDefault();

        if (!passwordModalUser?.id || newPassword.trim().length < 8 || isSavingPassword) {
            return;
        }

        try {
            setIsSavingPassword(true);
            await withAuth((token) => updateUser(token, passwordModalUser.id, { password: newPassword.trim() }));
            closePasswordModal();
            setPasswordModalUser(null);
            setNewPassword("");
            refreshTable();
        } finally {
            setIsSavingPassword(false);
        }
    };

    const handleCreateUserSubmit = async (event) => {
        event.preventDefault();

        if (!newUserEmail.trim() || newUserPassword.trim().length < 8 || isCreatingUser) {
            return;
        }

        try {
            setIsCreatingUser(true);
            setCreateUserError("");

            await withAuth((token) => createUser(token, {
                email: newUserEmail.trim(),
                password: newUserPassword.trim(),
                role: newUserRole.toUpperCase()
            }));

            closeCreateUserModal();
            refreshTable();
        } catch (err) {
            setCreateUserError(err?.message || "Impossible de créer l'utilisateur.");
        } finally {
            setIsCreatingUser(false);
        }
    };

    useImperativeHandle(ref, () => ({
        openCreateUserModal
    }), [openCreateUserModal]);

    return (
        <>
            <ServerSideDataTable
                key={tableKey}
                columns={columns}
                defaultSortBy={DEFAULT_SORT_BY}
                defaultSortIndex={DEFAULT_SORT_INDEX}
                defaultSortDir="asc"
                errorMessage="Impossible de charger les utilisateurs pour l'instant."
                registerDomHandlers={registerDomHandlers}
                fetchPage={(token, query) => getUsers(token, query.page, query.pageSize, {
                    q: query.q,
                    sortBy: query.sortBy,
                    sortDir: query.sortDir
                })}
            />

            <div className="modal fade" tabIndex="-1" aria-hidden="true" ref={createModalRef}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <form onSubmit={handleCreateUserSubmit}>
                            <div className="modal-header">
                                <h2 className="modal-title h6 mb-0">Créer un utilisateur</h2>
                                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Fermer" />
                            </div>
                            <div className="modal-body">
                                {createUserError && (
                                    <div className="alert alert-warning py-2">{createUserError}</div>
                                )}

                                <label className="form-label" htmlFor="create-user-email">Email</label>
                                <input
                                    id="create-user-email"
                                    type="email"
                                    className="form-control mb-3"
                                    value={newUserEmail}
                                    onChange={(event) => setNewUserEmail(event.target.value)}
                                    required
                                />

                                <label className="form-label" htmlFor="create-user-password">Mot de passe</label>
                                <input
                                    id="create-user-password"
                                    type="password"
                                    className="form-control mb-3"
                                    minLength={8}
                                    value={newUserPassword}
                                    onChange={(event) => setNewUserPassword(event.target.value)}
                                    required
                                />

                                <label className="form-label" htmlFor="create-user-role">Rôle</label>
                                <select
                                    id="create-user-role"
                                    className="form-select"
                                    value={newUserRole}
                                    onChange={(event) => setNewUserRole(event.target.value)}
                                >
                                    <option value="user">user</option>
                                    <option value="admin">admin</option>
                                </select>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Annuler</button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={isCreatingUser || !newUserEmail.trim() || newUserPassword.trim().length < 8}
                                >
                                    {isCreatingUser ? "Création..." : "Créer"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="modal fade" tabIndex="-1" aria-hidden="true" ref={emailModalRef}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <form onSubmit={handleEmailSubmit}>
                            <div className="modal-header">
                                <h2 className="modal-title h6 mb-0">Modifier l'email</h2>
                                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Fermer" />
                            </div>
                            <div className="modal-body">
                                <label className="form-label" htmlFor="new-user-email">Nouvel email</label>
                                <input
                                    id="new-user-email"
                                    type="email"
                                    className="form-control"
                                    value={newEmail}
                                    onChange={(event) => setNewEmail(event.target.value)}
                                    required
                                />
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Annuler</button>
                                <button type="submit" className="btn btn-primary" disabled={isSavingEmail || !newEmail.trim()}>
                                    {isSavingEmail ? "Enregistrement..." : "Enregistrer"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="modal fade" tabIndex="-1" aria-hidden="true" ref={modalRef}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <form onSubmit={handlePasswordSubmit}>
                            <div className="modal-header">
                                <h2 className="modal-title h6 mb-0">Modifier le mot de passe</h2>
                                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Fermer" />
                            </div>
                            <div className="modal-body">
                                <p className="small text-secondary mb-3">
                                    Utilisateur: <strong>{passwordModalUser?.email || "-"}</strong>
                                </p>
                                <label className="form-label" htmlFor="new-user-password">Nouveau mot de passe</label>
                                <input
                                    id="new-user-password"
                                    type="password"
                                    className="form-control"
                                    minLength={8}
                                    value={newPassword}
                                    onChange={(event) => setNewPassword(event.target.value)}
                                    required
                                />
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Annuler</button>
                                <button type="submit" className="btn btn-primary" disabled={isSavingPassword || newPassword.trim().length < 8}>
                                    {isSavingPassword ? "Enregistrement..." : "Enregistrer"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}

export default forwardRef(UsersDataTable);
